import React, { useEffect, useRef, useCallback } from 'react';
import { RefreshCw, MessageSquare, TrendingUp, CheckCircle } from 'lucide-react';
import FeedItem from './FeedItem';
import LoadingSpinner from './LoadingSpinner';
import SkeletonLoader from './SkeletonLoader';
import { useInteractions } from '../hooks/useInteractions';
import type { InteractionFeedProps } from '../types/interactions';

export default function InteractionFeed({ 
  userId, 
  limit = 20, 
  showSocialShare = true 
}: InteractionFeedProps) {
  const { 
    interactions, 
    loading, 
    error, 
    hasMore, 
    loadMore, 
    refresh, 
    shareMessage 
  } = useInteractions(limit);
  
  const observerRef = useRef<IntersectionObserver>();
  const lastElementRef = useRef<HTMLDivElement>(null);

  // Infinite scroll implementation
  const lastInteractionElementRef = useCallback((node: HTMLDivElement) => {
    if (loading) return;
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMore();
      }
    });
    
    if (node) observerRef.current.observe(node);
  }, [loading, hasMore, loadMore]);

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <MessageSquare className="h-8 w-8 text-red-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Unable to Load Feed
        </h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={refresh}
          className="bg-pink-500 text-white px-4 py-2 rounded-lg hover:bg-pink-600 transition-colors font-medium flex items-center space-x-2 mx-auto focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Try Again</span>
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <MessageSquare className="h-6 w-6 text-pink-400" />
          <h2 className="text-xl font-semibold text-gray-900">Your Accountability Journey</h2>
        </div>
        
        <button
          onClick={refresh}
          disabled={loading}
          className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
          aria-label="Refresh feed"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="text-sm font-medium">Refresh</span>
        </button>
      </div>

      {/* OneWin Social Follow Section */}
      <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl border border-pink-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <TrendingUp className="h-6 w-6 text-pink-500" />
          <h3 className="text-lg font-semibold text-gray-900">Stay Connected</h3>
        </div>
        <p className="text-gray-600 mb-4">
          Follow OneWin.Today for daily motivation, success stories, and productivity tips!
        </p>
        <div className="flex flex-wrap gap-3">
          <a
            href="https://linkedin.com/company/onewin-today"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <span>Follow on LinkedIn</span>
          </a>
          <a
            href="https://twitter.com/onewintoday"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
          >
            <span>Follow on Twitter</span>
          </a>
        </div>
      </div>

      {/* Feed Content */}
      {interactions.length === 0 && !loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Interactions Yet
          </h3>
          <p className="text-gray-600 mb-4">
            Your conversations with your AI coach will appear here. Start by setting your first daily goal!
          </p>
          
          {/* Sample Data for Testing */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm font-medium mb-2">
              🧪 Testing Mode: Sample Data Available
            </p>
            <button
              onClick={() => {
                // This would normally load sample data for testing
                console.log('Loading sample interaction data for testing...');
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Load Sample Data
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {interactions.map((interaction, index) => (
            <div
              key={interaction.id}
              ref={index === interactions.length - 1 ? lastInteractionElementRef : undefined}
            >
              <FeedItem
                message={interaction}
                onShare={shareMessage}
                showShareButtons={showSocialShare}
              />
            </div>
          ))}
          
          {/* Loading More Indicator */}
          {loading && (
            <div className="flex justify-center py-8">
              <div className="flex items-center space-x-3">
                <LoadingSpinner size="sm" color="pink" />
                <span className="text-gray-600">Loading more interactions...</span>
              </div>
            </div>
          )}
          
          {/* End of Feed Indicator */}
          {!hasMore && interactions.length > 0 && (
            <div className="text-center py-8">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-100 rounded-full">
                <CheckCircle className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">You've reached the beginning of your journey!</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Initial Loading State */}
      {loading && interactions.length === 0 && (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <SkeletonLoader variant="circular" width={20} height={20} />
                  <SkeletonLoader width={120} height={16} />
                </div>
                <SkeletonLoader width={80} height={16} />
              </div>
              <SkeletonLoader lines={3} />
              <SkeletonLoader width="60%" height={16} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}